<template>
  <div>
    <v-breadcrumbs
      :items="breadcrumbs"
      divider="/"
      class="pa-0 mb-4"
    >
      <template #item="{ item }">
        <v-breadcrumbs-item
          :to="item.to"
          :disabled="item.disabled"
          exact
        >
          {{ item.text }}
        </v-breadcrumbs-item>
      </template>
    </v-breadcrumbs>

    <div class="d-flex align-start mb-6">
      <div class="flex-grow-1">
        <p class="text-2xl mb-1">
          Рейтинг
        </p>
        <p class="text--secondary mb-0">
          {{ sectionTitle }}
        </p>
      </div>
    </div>

    <v-tabs
      :value="sectionIndex"
      class="mb-5"
      @change="onSectionChange"
    >
      <v-tab>
        Авторизованные тренеры
      </v-tab>
      <v-tab>
        Критерии допуска на тренировки
      </v-tab>
    </v-tabs>

    <div v-if="section === 'coaches'">
      <v-alert
        v-if="coachesError"
        type="error"
        text
        dense
        class="mb-4"
      >
        {{ coachesError }}
      </v-alert>

      <v-alert
        v-if="saveError"
        type="error"
        text
        dense
        class="mb-4"
      >
        {{ saveError }}
      </v-alert>

      <v-card
        v-if="coachesLoading && !coaches.length"
        flat
        outlined
        class="pa-4"
      >
        <v-progress-linear
          indeterminate
          color="primary"
          class="mb-4"
        />
        <v-skeleton-loader type="table" />
      </v-card>

      <v-card
        v-else
        flat
        outlined
      >
        <v-data-table
          :headers="coachHeaders"
          :items="filteredCoaches"
          :loading="coachesLoading"
          :page.sync="coachTablePage"
          item-key="id"
          :no-data-text="coachNoDataText"
          :items-per-page="20"
        >
          <template #[`header.full_name`]>
            <div class="d-flex align-center">
              <span class="text-no-wrap mr-3">Тренер</span>
              <v-text-field
                v-model="coachNameQuery"
                dense
                outlined
                hide-details
                single-line
                clearable
                placeholder="Имя или фамилия"
                autocomplete="off"
                class="coach-name-filter mt-0 pt-0"
                @click.native.stop
                @mousedown.native.stop
              />
            </div>
          </template>

          <template #[`item.full_name`]="{ item }">
            <div>{{ item.full_name }}</div>
            <div
              v-if="item.email || item.phone"
              class="text--secondary text-caption"
            >
              <span v-if="item.email">{{ item.email }}</span>
              <span v-if="item.email && item.phone"> · </span>
              <span v-if="item.phone">{{ item.phone }}</span>
            </div>
          </template>

          <template #[`item.can_rate_badminton`]="{ item }">
            <v-switch
              v-model="item.can_rate_badminton"
              hide-details
              dense
              class="mt-0"
              :disabled="savingId === item.id"
            />
          </template>

          <template #[`item.can_rate_table_tennis`]="{ item }">
            <v-switch
              v-model="item.can_rate_table_tennis"
              hide-details
              dense
              class="mt-0"
              :disabled="savingId === item.id"
            />
          </template>

          <template #[`item.can_rate_squash`]="{ item }">
            <v-switch
              v-model="item.can_rate_squash"
              hide-details
              dense
              class="mt-0"
              :disabled="savingId === item.id"
            />
          </template>

          <template #[`item.actions`]="{ item }">
            <v-btn
              small
              color="primary"
              :disabled="!isCoachDirty(item) || savingId === item.id"
              :loading="savingId === item.id"
              @click="saveCoach(item)"
            >
              Сохранить
            </v-btn>
          </template>
        </v-data-table>
      </v-card>
    </div>

    <div v-if="section === 'criteria'">
      <div class="d-flex justify-end mb-4">
        <v-btn
          color="primary"
          @click="openAddCriterion"
        >
          Добавить
        </v-btn>
      </div>

      <v-alert
        v-if="criteriaError"
        type="error"
        text
        dense
        class="mb-4"
      >
        {{ criteriaError }}
      </v-alert>

      <v-card
        v-if="criteriaLoading && !criteria.length"
        flat
        outlined
        class="pa-4"
      >
        <v-progress-linear
          indeterminate
          color="primary"
          class="mb-4"
        />
        <v-skeleton-loader type="table" />
      </v-card>

      <v-card
        v-else
        flat
        outlined
      >
        <v-data-table
          :headers="criterionHeaders"
          :items="criteria"
          :loading="criteriaLoading"
          item-key="id"
          no-data-text="Критериев нет"
          :items-per-page="20"
        >
          <template #[`item.rule`]="{ item }">
            {{ ruleText(item) }}
          </template>

          <template #[`item.is_active`]="{ item }">
            <v-switch
              :input-value="item.is_active"
              hide-details
              dense
              class="mt-0"
              :disabled="togglingId === item.id || deletingId === item.id"
              @change="setCriterionActive(item, $event)"
            />
          </template>

          <template #[`item.actions`]="{ item }">
            <v-btn
              small
              text
              color="primary"
              :disabled="deletingId === item.id"
              @click="openTagEvents(item)"
            >
              События
            </v-btn>
            <v-btn
              small
              text
              color="primary"
              :loading="openingId === item.id"
              :disabled="deletingId === item.id"
              @click="openCriterion(item)"
            >
              Открыть
            </v-btn>
            <v-btn
              small
              text
              color="error"
              :loading="deletingId === item.id"
              :disabled="openingId === item.id || togglingId === item.id"
              @click="deleteCriterion(item)"
            >
              Удалить
            </v-btn>
          </template>
        </v-data-table>
      </v-card>

      <v-dialog
        v-model="criterionDialog"
        max-width="800"
        :persistent="criterionSaving"
      >
        <v-card v-if="criterionForm">
          <v-form @submit.prevent="saveCriterionForm">
            <v-card-title>
              Критерий допуска
            </v-card-title>
            <v-card-text>
              <div class="mb-4">
                <div class="text-caption text--secondary">
                  Филиал
                </div>
                <div>{{ criterionForm.company_title }}</div>
              </div>
              <div class="mb-4">
                <div class="text-caption text--secondary">
                  Тег
                </div>
                <div>{{ criterionForm.label_title }}</div>
                <div
                  v-if="criterionErrors.label_title"
                  class="error--text text-caption"
                >
                  {{ criterionErrors.label_title }}
                </div>
              </div>

              <v-select
                v-model="criterionForm.sport"
                :items="sports"
                item-text="title"
                item-value="value"
                label="Вид спорта"
                outlined
                dense
                hide-details="auto"
                class="mb-4"
                :disabled="criterionSaving"
                :error-messages="criterionErrors.sport ? [criterionErrors.sport] : []"
                @change="onCriterionSportChange"
              />

              <v-select
                v-model="criterionForm.rule"
                :items="rules"
                item-text="title"
                item-value="value"
                label="Правило"
                outlined
                dense
                hide-details="auto"
                class="mb-4"
                :disabled="criterionSaving"
                :error-messages="criterionErrors.rule ? [criterionErrors.rule] : []"
                @change="onCriterionRuleChange"
              />

              <v-text-field
                v-if="showRatingMin"
                v-model="criterionForm.rating_min"
                label="От"
                outlined
                dense
                hide-details="auto"
                class="mb-4"
                :hint="boundHint"
                persistent-hint
                :disabled="criterionSaving"
                :error-messages="criterionErrors.rating_min ? [criterionErrors.rating_min] : []"
                @input="clearCriterionFieldError('rating_min')"
              />

              <v-text-field
                v-if="showRatingMax"
                v-model="criterionForm.rating_max"
                label="До"
                outlined
                dense
                hide-details="auto"
                class="mb-4"
                :hint="boundHint"
                persistent-hint
                :disabled="criterionSaving"
                :error-messages="criterionErrors.rating_max ? [criterionErrors.rating_max] : []"
                @input="clearCriterionFieldError('rating_max')"
              />

              <v-switch
                v-model="criterionForm.is_active"
                label="Включён"
                hide-details="auto"
                dense
                class="mt-0"
                :disabled="criterionSaving"
                :error-messages="criterionErrors.is_active ? [criterionErrors.is_active] : []"
                @change="clearCriterionFieldError('is_active')"
              />

              <v-divider class="my-4" />

              <div class="text-subtitle-2 mb-1">
                Групповые события с этим тегом
              </div>
              <div class="text-caption text--secondary mb-3">
                {{ tagEventsPeriod || 'Ближайшие 30 дней' }}
              </div>
              <v-progress-linear
                v-if="tagEventsLoading"
                indeterminate
                color="primary"
                class="mb-3"
              />
              <v-alert
                v-if="tagEventsError"
                type="error"
                text
                dense
                class="mb-0"
              >
                {{ tagEventsError }}
              </v-alert>
              <v-alert
                v-else-if="!tagEventsLoading && !tagEvents.length"
                type="info"
                text
                dense
                class="mb-0"
              >
                {{ tagEventsMessage || 'События с этим тегом на ближайшие 30 дней не найдены' }}
              </v-alert>
              <v-data-table
                v-else-if="tagEvents.length"
                :headers="previewHeaders"
                :items="tagEvents"
                item-key="_key"
                dense
                hide-default-footer
                :items-per-page="-1"
                class="tag-events-table"
              />

              <v-alert
                v-if="criterionError"
                type="error"
                text
                dense
                class="mt-4 mb-0"
              >
                {{ criterionError }}
              </v-alert>
            </v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn
                text
                type="button"
                :disabled="criterionSaving"
                @click="criterionDialog = false"
              >
                Закрыть
              </v-btn>
              <v-btn
                color="primary"
                type="submit"
                :loading="criterionSaving"
              >
                Сохранить
              </v-btn>
            </v-card-actions>
          </v-form>
        </v-card>
      </v-dialog>

      <v-dialog
        v-model="deleteDialog"
        max-width="420"
      >
        <v-card>
          <v-card-title>
            Удалить критерий?
          </v-card-title>
          <v-card-text v-if="deleteTarget">
            «{{ deleteTarget.label_title }}» ({{ deleteTarget.company_title }})
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn
              text
              :disabled="deletingId !== null"
              @click="deleteDialog = false"
            >
              Отмена
            </v-btn>
            <v-btn
              text
              color="error"
              :loading="deletingId !== null"
              @click="confirmDeleteCriterion"
            >
              Удалить
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog
        v-model="addDialog"
        max-width="800"
      >
        <v-card>
          <v-card-title>
            Новый критерий
          </v-card-title>
          <v-card-text>
            <v-select
              v-model="selectedCompanyId"
              :items="companies"
              item-text="title"
              item-value="company_id"
              label="Филиал"
              outlined
              dense
              hide-details
              class="mb-4"
              @change="onCompanyChange"
            />
            <v-select
              v-model="selectedLabelId"
              :items="labels"
              :item-text="labelOptionText"
              item-value="yc_label_id"
              label="Тег"
              outlined
              dense
              hide-details
              no-data-text="Тегов нет"
              :disabled="!selectedCompanyId || labelsLoading"
              :loading="labelsLoading"
              class="mb-4"
              @change="onLabelChange"
            />

            <v-alert
              v-if="labelsError"
              type="error"
              text
              dense
              class="mb-4"
            >
              {{ labelsError }}
            </v-alert>

            <v-progress-linear
              v-if="labelLookupLoading || previewLoading"
              indeterminate
              color="primary"
              class="mb-4"
            />

            <v-alert
              v-if="previewError"
              type="error"
              text
              dense
              class="mb-4"
            >
              {{ previewError }}
            </v-alert>

            <div
              v-if="previewReady && previewPeriod"
              class="text-caption text--secondary mb-2"
            >
              {{ previewPeriod }}
            </div>

            <v-alert
              v-if="previewReady && !previewEvents.length && previewMessage"
              type="info"
              text
              dense
              class="mb-4"
            >
              {{ previewMessage }}
            </v-alert>

            <v-data-table
              v-if="previewEvents.length"
              :headers="previewHeaders"
              :items="previewEvents"
              item-key="_key"
              :items-per-page="-1"
              hide-default-footer
              class="mb-4"
            />

            <v-btn
              v-if="previewReady"
              color="primary"
              @click="openNewCriterionForm"
            >
              Добавить допуски по рейтингу
            </v-btn>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn
              text
              @click="addDialog = false"
            >
              Закрыть
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog
        v-model="eventsDialog"
        max-width="800"
      >
        <v-card>
          <v-card-title>
            {{ eventsDialogTitle }}
          </v-card-title>
          <v-card-subtitle v-if="eventsDialogCompany">
            {{ eventsDialogCompany }}
          </v-card-subtitle>
          <v-card-text>
            <div class="text-caption text--secondary mb-3">
              {{ tagEventsPeriod || 'Ближайшие 30 дней' }}
            </div>
            <v-progress-linear
              v-if="tagEventsLoading"
              indeterminate
              color="primary"
              class="mb-3"
            />
            <v-alert
              v-if="tagEventsError"
              type="error"
              text
              dense
              class="mb-0"
            >
              {{ tagEventsError }}
            </v-alert>
            <v-alert
              v-else-if="!tagEventsLoading && !tagEvents.length"
              type="info"
              text
              dense
              class="mb-0"
            >
              {{ tagEventsMessage || 'События с этим тегом на ближайшие 30 дней не найдены' }}
            </v-alert>
            <v-data-table
              v-else-if="tagEvents.length"
              :headers="previewHeaders"
              :items="tagEvents"
              item-key="_key"
              dense
              hide-default-footer
              :items-per-page="-1"
              class="tag-events-table"
            />
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn
              text
              @click="eventsDialog = false"
            >
              Закрыть
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </div>
  </div>
</template>

<script>
import {
  createRatingCriterion,
  deleteRatingCriterion,
  getRatingCoaches,
  getRatingCriteria,
  getRatingCriterion,
  getRatingLabels,
  getRatingPreview,
  updateRatingCoach,
  updateRatingCriterion,
} from '@/api/rating'

const COMPANIES = [
  { company_id: 230691, title: 'Space Химки' },
  { company_id: 232685, title: 'Залы Khimki BC' },
  { company_id: 992575, title: 'Space ВДНХ' },
]

const SECTION_TITLES = {
  coaches: 'Авторизованные тренеры',
  criteria: 'Критерии допуска на тренировки',
}

const FLAG_KEYS = [
  'can_rate_badminton',
  'can_rate_table_tennis',
  'can_rate_squash',
]

function filterCoachesByName(coaches, query) {
  const text = String(query || '').trim().toLocaleLowerCase('ru')
  if (text.length < 3) return coaches
  return coaches.filter(coach => String(coach.full_name || '').toLocaleLowerCase('ru').includes(text))
}

function flagsOf(coach) {
  return {
    can_rate_badminton: Boolean(coach.can_rate_badminton),
    can_rate_table_tennis: Boolean(coach.can_rate_table_tennis),
    can_rate_squash: Boolean(coach.can_rate_squash),
  }
}

function changedFlags(coach, saved) {
  const payload = {}
  FLAG_KEYS.forEach(key => {
    if (Boolean(coach[key]) !== Boolean(saved[key])) {
      payload[key] = Boolean(coach[key])
    }
  })
  return payload
}

function formatRuleBounds(item) {
  if (item.rule === 'MIN') return `от ${item.rating_min}`
  if (item.rule === 'MAX') return `до ${item.rating_max}`
  if (item.rule === 'RANGE') return `от ${item.rating_min} до ${item.rating_max}`
  return item.rule_title || ''
}

function apiErrorMessage(error, fallback) {
  const data = error && error.response && error.response.data
  if (!data) return fallback
  if (typeof data.detail === 'string') return data.detail
  if (Array.isArray(data.detail)) return data.detail.map(String).join(', ')
  if (typeof data === 'object') {
    const text = Object.entries(data).map(([key, value]) => {
      const message = Array.isArray(value) ? value.map(String).join(', ') : String(value)
      return key === 'detail' ? message : `${key}: ${message}`
    }).join('; ')
    if (text) return text
  }
  return fallback
}

const SPORTS = [
  { value: 'BADMINTON', title: 'Бадминтон' },
  { value: 'TABLE_TENNIS', title: 'Настольный теннис' },
  { value: 'SQUASH', title: 'Сквош' },
]

const RULES = [
  { value: 'MIN', title: 'Рейтинг от' },
  { value: 'MAX', title: 'Рейтинг до' },
  { value: 'RANGE', title: 'Рейтинг от / до' },
]

const CRITERION_FIELDS = ['sport', 'rule', 'rating_min', 'rating_max', 'label_title', 'is_active']

const INTEGER_BOUND = /^[1-9]\d{0,4}$/
const SQUASH_BOUND = /^(?:0\.(?:0[1-9]|[1-9]\d)|[1-9]\.\d{2})$/

function labelTitleOf(label) {
  if (!label) return ''
  return label.label_title || label.title || ''
}

function sportByTitle(title) {
  const sport = SPORTS.find(item => item.title === title)
  return sport ? sport.value : null
}

function companyTitle(companyId) {
  const company = COMPANIES.find(item => item.company_id === Number(companyId))
  return company ? company.title : ''
}

function presentCriterion(criterion) {
  const sport = SPORTS.find(item => item.value === criterion.sport)
  return {
    ...criterion,
    company_title: criterion.company_title || companyTitle(criterion.company_id),
    sport_title: (sport && sport.title) || criterion.sport_title || '',
  }
}

function displayBound(value, sport) {
  if (value == null || value === '') return ''
  const text = String(value).trim()
  if (sport !== 'SQUASH' || !/^\d+(\.\d+)?$/.test(text)) return text
  const number = Number(text)
  if (!Number.isFinite(number)) return text
  return number.toFixed(2)
}

function formFromCriterion(criterion) {
  const item = presentCriterion(criterion)
  const sport = item.sport || sportByTitle(item.sport_title)
  return {
    id: item.id,
    company_id: item.company_id,
    company_title: item.company_title,
    yc_label_id: item.yc_label_id,
    label_title: item.label_title || '',
    sport,
    rule: item.rule || null,
    rating_min: displayBound(item.rating_min, sport),
    rating_max: displayBound(item.rating_max, sport),
    is_active: item.is_active == null ? true : Boolean(item.is_active),
  }
}

function normalizeBound(value) {
  return String(value == null ? '' : value).trim().replace(',', '.')
}

function boundMessage(value, sport) {
  const text = normalizeBound(value)
  if (!text) return 'Укажите число'
  if (!sport) return ''
  if (sport === 'SQUASH') {
    if (!SQUASH_BOUND.test(text)) return 'Число от 0.01 до 9.99 с двумя знаками'
    return ''
  }
  if (!INTEGER_BOUND.test(text)) return 'Целое число от 1 до 99999'
  return ''
}

function boundSortValue(value, sport) {
  const text = normalizeBound(value)
  if (sport === 'SQUASH') return Math.round(Number(text) * 100)
  return Number(text)
}

function validateCriterionForm(form) {
  const errors = {}
  if (!form.sport) errors.sport = 'Выберите вид спорта'
  if (!form.rule) errors.rule = 'Выберите правило'
  const needsMin = form.rule === 'MIN' || form.rule === 'RANGE'
  const needsMax = form.rule === 'MAX' || form.rule === 'RANGE'
  if (needsMin) {
    const message = boundMessage(form.rating_min, form.sport)
    if (message) errors.rating_min = message
  }
  if (needsMax) {
    const message = boundMessage(form.rating_max, form.sport)
    if (message) errors.rating_max = message
  }
  if (
    form.rule === 'RANGE'
    && !errors.rating_min
    && !errors.rating_max
    && boundSortValue(form.rating_min, form.sport) > boundSortValue(form.rating_max, form.sport)
  ) {
    errors.rating_min = 'Нижняя граница не выше верхней'
  }
  return errors
}

function criterionBounds(form) {
  const bounds = {}
  if (form.rule === 'MIN' || form.rule === 'RANGE') {
    bounds.rating_min = normalizeBound(form.rating_min)
  }
  if (form.rule === 'MAX' || form.rule === 'RANGE') {
    bounds.rating_max = normalizeBound(form.rating_max)
  }
  return bounds
}

function splitApiError(error, fallback) {
  const data = error && error.response && error.response.data
  const fields = {}
  const extras = []
  let detail = ''
  if (data && typeof data === 'object' && !Array.isArray(data)) {
    if (typeof data.detail === 'string') detail = data.detail
    else if (Array.isArray(data.detail)) detail = data.detail.map(String).join(', ')
    Object.entries(data).forEach(([key, value]) => {
      if (key === 'detail') return
      const message = Array.isArray(value) ? value.map(String).join(', ') : String(value)
      if (CRITERION_FIELDS.includes(key)) fields[key] = message
      else extras.push(`${key}: ${message}`)
    })
  }
  let general = detail
  if (extras.length) general = general ? `${general}; ${extras.join('; ')}` : extras.join('; ')
  if (!general && !Object.keys(fields).length) general = fallback
  return { general, fields }
}

function formatIsoDate(value) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value || ''))
  if (!match) return value || ''
  return `${match[3]}.${match[2]}.${match[1]}`
}

function formatPreviewPeriod(data) {
  if (!data || !data.date_from || !data.date_to) return ''
  return `${formatIsoDate(data.date_from)} — ${formatIsoDate(data.date_to)}`
}

function mapPreviewEvents(events) {
  return (events || []).map((event, index) => ({
    ...event,
    date_label: formatIsoDate(event.date),
    _key: `${event.yc_activity_id || 'event'}-${event.datetime || index}`,
  }))
}

export default {
  data() {
    return {
      activeSection: this.$route.params.section,
      coaches: [],
      coachNameQuery: '',
      coachTablePage: 1,
      savedFlags: {},
      coachesLoading: false,
      coachesError: '',
      saveError: '',
      savingId: null,
      coachesRequestId: 0,
      coachHeaders: [
        {
          text: 'Тренер',
          value: 'full_name',
          sortable: false,
          width: 420,
        },
        {
          text: 'Бадминтон',
          value: 'can_rate_badminton',
          sortable: false,
          width: 140,
        },
        {
          text: 'Настольный теннис',
          value: 'can_rate_table_tennis',
          sortable: false,
          width: 190,
        },
        {
          text: 'Сквош',
          value: 'can_rate_squash',
          sortable: false,
          width: 120,
        },
        {
          text: '',
          value: 'actions',
          sortable: false,
          align: 'end',
          width: 150,
        },
      ],
      criteria: [],
      criteriaLoading: false,
      criteriaError: '',
      criteriaRequestId: 0,
      togglingId: null,
      deletingId: null,
      openingId: null,
      criterionDialog: false,
      criterionForm: null,
      criterionErrors: {},
      criterionError: '',
      criterionSaving: false,
      sports: SPORTS,
      rules: RULES,
      deleteDialog: false,
      deleteTarget: null,
      criterionHeaders: [
        { text: 'Филиал', value: 'company_title' },
        { text: 'Тег', value: 'label_title' },
        { text: 'Вид спорта', value: 'sport_title' },
        {
          text: 'Правило',
          value: 'rule',
          sortable: false,
        },
        {
          text: 'Включён',
          value: 'is_active',
          sortable: false,
          width: 120,
        },
        {
          text: '',
          value: 'actions',
          sortable: false,
          align: 'end',
          width: 300,
        },
      ],
      companies: COMPANIES,
      addDialog: false,
      selectedCompanyId: null,
      labels: [],
      labelsLoading: false,
      labelsError: '',
      labelsRequestId: 0,
      selectedLabelId: null,
      labelLookupLoading: false,
      labelLookupRequestId: 0,
      previewEvents: [],
      previewLoading: false,
      previewError: '',
      previewMessage: '',
      previewPeriod: '',
      previewPayload: null,
      previewReady: false,
      previewRequestId: 0,
      eventsDialog: false,
      eventsDialogTitle: '',
      eventsDialogCompany: '',
      tagEvents: [],
      tagEventsLoading: false,
      tagEventsError: '',
      tagEventsMessage: '',
      tagEventsPeriod: '',
      tagEventsRequestId: 0,
      previewHeaders: [
        { text: 'Дата', value: 'date_label', sortable: false },
        { text: 'Время', value: 'time', sortable: false },
        { text: 'Название', value: 'title', sortable: false },
        { text: 'Филиал', value: 'company_title', sortable: false },
      ],
    }
  },
  computed: {
    filteredCoaches() {
      return filterCoachesByName(this.coaches, this.coachNameQuery)
    },
    coachNoDataText() {
      const query = String(this.coachNameQuery || '').trim()
      if (query.length >= 3) return 'Ничего не найдено'
      return 'Тренеров нет'
    },
    section() {
      return this.activeSection
    },
    sectionIndex() {
      return this.activeSection === 'criteria' ? 1 : 0
    },
    sectionTitle() {
      return SECTION_TITLES[this.section] || SECTION_TITLES.coaches
    },
    breadcrumbs() {
      return [
        { text: 'Рейтинг', to: '/rating/coaches', disabled: false },
        { text: this.sectionTitle, disabled: true },
      ]
    },
    showRatingMin() {
      const rule = this.criterionForm && this.criterionForm.rule
      return rule === 'MIN' || rule === 'RANGE'
    },
    showRatingMax() {
      const rule = this.criterionForm && this.criterionForm.rule
      return rule === 'MAX' || rule === 'RANGE'
    },
    boundHint() {
      const sport = this.criterionForm && this.criterionForm.sport
      if (sport === 'SQUASH') return 'От 0.01 до 9.99, два знака'
      if (sport === 'BADMINTON' || sport === 'TABLE_TENNIS') return 'Целое от 1 до 99999'
      return ''
    },
  },
  watch: {
    coachNameQuery() {
      this.coachTablePage = 1
    },
    '$route.params.section'(section) {
      if (section === 'coaches' || section === 'criteria') {
        this.activeSection = section
      }
    },
    section: {
      immediate: true,
      handler(section) {
        if (section !== 'criteria') {
          this.addDialog = false
          this.criterionDialog = false
          this.eventsDialog = false
        }
        if (section === 'coaches') this.loadCoaches()
        if (section === 'criteria') this.loadCriteria()
      },
    },
  },
  methods: {
    onSectionChange(index) {
      const next = index === 1 ? 'criteria' : 'coaches'
      if (next === this.activeSection) return
      this.activeSection = next
      if (this.$route.params.section === next) return
      this.$router.push(`/rating/${next}`).catch(() => {})
    },
    async loadCoaches() {
      const requestId = this.coachesRequestId + 1
      this.coachesRequestId = requestId
      this.coachesLoading = true
      this.coachesError = ''
      this.saveError = ''
      try {
        const data = await getRatingCoaches()
        if (this.coachesRequestId !== requestId) return
        const results = (data && data.results) || []
        this.coaches = results.map(coach => ({ ...coach }))
        const saved = {}
        results.forEach(coach => {
          saved[coach.id] = flagsOf(coach)
        })
        this.savedFlags = saved
      } catch (error) {
        if (this.coachesRequestId !== requestId) return
        this.coaches = []
        this.savedFlags = {}
        this.coachesError = apiErrorMessage(error, 'Не удалось загрузить тренеров')
      } finally {
        if (this.coachesRequestId === requestId) this.coachesLoading = false
      }
    },
    isCoachDirty(coach) {
      const saved = this.savedFlags[coach.id]
      if (!saved) return false
      return Object.keys(changedFlags(coach, saved)).length > 0
    },
    async saveCoach(coach) {
      const saved = this.savedFlags[coach.id]
      const payload = saved ? changedFlags(coach, saved) : {}
      if (!Object.keys(payload).length) return

      this.savingId = coach.id
      this.saveError = ''
      try {
        const updated = await updateRatingCoach(coach.id, payload)
        const index = this.coaches.findIndex(item => item.id === coach.id)
        if (index !== -1) this.$set(this.coaches, index, { ...updated })
        this.$set(this.savedFlags, updated.id, flagsOf(updated))
      } catch (error) {
        this.saveError = apiErrorMessage(error, 'Не удалось сохранить права')
      } finally {
        this.savingId = null
      }
    },
    ruleText(item) {
      return formatRuleBounds(item)
    },
    async loadCriteria() {
      const requestId = this.criteriaRequestId + 1
      this.criteriaRequestId = requestId
      this.criteriaLoading = true
      this.criteriaError = ''
      try {
        const data = await getRatingCriteria()
        if (this.criteriaRequestId !== requestId) return
        this.criteria = ((data && data.results) || []).map(item => ({ ...item }))
      } catch (error) {
        if (this.criteriaRequestId !== requestId) return
        this.criteria = []
        this.criteriaError = apiErrorMessage(error, 'Не удалось загрузить критерии')
      } finally {
        if (this.criteriaRequestId === requestId) this.criteriaLoading = false
      }
    },
    replaceCriterion(updated) {
      const criterion = presentCriterion(updated)
      const index = this.criteria.findIndex(item => item.id === criterion.id)
      if (index !== -1) this.$set(this.criteria, index, { ...criterion })
      if (this.criterionForm && this.criterionForm.id === criterion.id) {
        this.criterionForm.is_active = criterion.is_active
      }
    },
    upsertCriterion(criterion) {
      const item = presentCriterion(criterion)
      const index = this.criteria.findIndex(row => row.id === item.id)
      if (index === -1) this.criteria.unshift({ ...item })
      else this.$set(this.criteria, index, { ...item })
    },
    showCriterionForm(criterion) {
      this.criterionForm = formFromCriterion(criterion)
      this.criterionErrors = {}
      this.criterionError = ''
      this.criterionDialog = true
      const form = this.criterionForm
      if (form.company_id != null && form.yc_label_id != null) {
        this.loadTagEvents(form.company_id, form.yc_label_id)
      }
    },
    acceptSavedCriterion(criterion, close) {
      const form = this.criterionForm || {}
      const saved = {
        ...criterion,
        company_id: criterion.company_id != null ? criterion.company_id : form.company_id,
        company_title: criterion.company_title || form.company_title,
        yc_label_id: criterion.yc_label_id != null ? criterion.yc_label_id : form.yc_label_id,
        label_title: criterion.label_title || form.label_title,
      }
      this.upsertCriterion(saved)
      if (close) {
        this.criterionErrors = {}
        this.criterionError = ''
        this.criterionSaving = false
        this.criterionDialog = false
        return
      }
      this.showCriterionForm(saved)
    },
    labelOptionText(label) {
      return labelTitleOf(label)
    },
    clearCriterionFieldError(field) {
      if (!this.criterionErrors[field]) return
      const next = { ...this.criterionErrors }
      delete next[field]
      this.criterionErrors = next
    },
    onCriterionSportChange() {
      this.clearCriterionFieldError('sport')
      this.clearCriterionFieldError('rating_min')
      this.clearCriterionFieldError('rating_max')
    },
    onCriterionRuleChange() {
      this.clearCriterionFieldError('rule')
      this.clearCriterionFieldError('rating_min')
      this.clearCriterionFieldError('rating_max')
    },
    openNewCriterionForm() {
      const company = this.companies.find(item => item.company_id === this.selectedCompanyId)
      const label = this.labels.find(item => item.yc_label_id === this.selectedLabelId)
      if (!company || !label) return
      this.criterionForm = {
        id: null,
        company_id: company.company_id,
        company_title: company.title,
        yc_label_id: label.yc_label_id,
        label_title: labelTitleOf(label),
        sport: null,
        rule: null,
        rating_min: '',
        rating_max: '',
        is_active: true,
      }
      this.criterionErrors = {}
      this.criterionError = ''
      this.addDialog = false
      if (this.previewPayload) {
        this.tagEventsRequestId += 1
        this.applyTagEvents(this.previewPayload)
      } else {
        this.loadTagEvents(company.company_id, label.yc_label_id)
      }
      this.$nextTick(() => {
        this.criterionDialog = true
      })
    },
    async saveCriterionForm() {
      if (this.criterionSaving || !this.criterionForm) return
      const form = this.criterionForm
      const errors = validateCriterionForm(form)
      this.criterionErrors = errors
      this.criterionError = ''
      if (Object.keys(errors).length) return
      if (!form.id && (form.company_id == null || form.yc_label_id == null || !form.label_title)) {
        this.criterionError = 'Не выбран филиал или тег'
        return
      }

      const editable = {
        sport: form.sport,
        rule: form.rule,
        is_active: form.is_active,
        ...criterionBounds(form),
      }
      if (form.label_title) editable.label_title = form.label_title

      this.criterionSaving = true
      try {
        if (form.id) {
          const updated = await updateRatingCriterion(form.id, editable)
          this.acceptSavedCriterion(updated, true)
          return
        }
        const data = await createRatingCriterion({
          company_id: form.company_id,
          yc_label_id: form.yc_label_id,
          ...editable,
        })
        if (!data || !data.criterion) {
          this.criterionError = 'Не удалось сохранить критерий'
          return
        }
        this.acceptSavedCriterion(data.criterion, true)
      } catch (error) {
        const body = error.response && error.response.data
        if (error.response && error.response.status === 409 && body && body.criterion) {
          this.acceptSavedCriterion(body.criterion)
          return
        }
        const split = splitApiError(error, 'Не удалось сохранить критерий')
        this.criterionError = split.general
        this.criterionErrors = split.fields
      } finally {
        this.criterionSaving = false
      }
    },
    async openCriterion(item) {
      this.openingId = item.id
      this.criteriaError = ''
      try {
        const criterion = await getRatingCriterion(item.id)
        this.showCriterionForm(criterion)
      } catch (error) {
        this.criteriaError = apiErrorMessage(error, 'Не удалось открыть критерий')
      } finally {
        this.openingId = null
      }
    },
    async setCriterionActive(item, isActive) {
      if (this.togglingId === item.id || item.is_active === isActive) return
      const previous = item.is_active
      this.$set(item, 'is_active', isActive)
      this.togglingId = item.id
      this.criteriaError = ''
      try {
        const updated = await updateRatingCriterion(item.id, { is_active: isActive })
        this.replaceCriterion(updated)
      } catch (error) {
        this.$set(item, 'is_active', previous)
        this.criteriaError = apiErrorMessage(error, 'Не удалось изменить критерий')
      } finally {
        this.togglingId = null
      }
    },
    deleteCriterion(item) {
      this.deleteTarget = item
      this.deleteDialog = true
    },
    async confirmDeleteCriterion() {
      const item = this.deleteTarget
      if (!item) return

      this.deletingId = item.id
      this.criteriaError = ''
      try {
        await deleteRatingCriterion(item.id)
        this.criteria = this.criteria.filter(row => row.id !== item.id)
        if (this.criterionForm && this.criterionForm.id === item.id) {
          this.criterionForm = null
          this.criterionDialog = false
        }
        this.deleteDialog = false
        this.deleteTarget = null
      } catch (error) {
        this.criteriaError = apiErrorMessage(error, 'Не удалось удалить критерий')
        this.deleteDialog = false
      } finally {
        this.deletingId = null
      }
    },
    openAddCriterion() {
      this.selectedCompanyId = null
      this.selectedLabelId = null
      this.labels = []
      this.labelsError = ''
      this.labelsLoading = false
      this.labelLookupLoading = false
      this.resetPreview()
      this.addDialog = true
    },
    resetPreview() {
      this.previewRequestId += 1
      this.previewEvents = []
      this.previewLoading = false
      this.previewError = ''
      this.previewMessage = ''
      this.previewPeriod = ''
      this.previewPayload = null
      this.previewReady = false
    },
    async onCompanyChange(companyId) {
      this.selectedLabelId = null
      this.labels = []
      this.labelsError = ''
      this.labelLookupLoading = false
      this.labelLookupRequestId += 1
      this.resetPreview()
      if (!companyId) return

      const requestId = this.labelsRequestId + 1
      this.labelsRequestId = requestId
      this.labelsLoading = true
      try {
        const data = await getRatingLabels(companyId)
        if (this.labelsRequestId !== requestId) return
        if (this.selectedCompanyId !== companyId) return
        this.labels = (data && data.results) || []
      } catch (error) {
        if (this.labelsRequestId !== requestId) return
        this.labels = []
        this.labelsError = apiErrorMessage(error, 'Не удалось загрузить теги')
      } finally {
        if (this.labelsRequestId === requestId) this.labelsLoading = false
      }
    },
    async onLabelChange(ycLabelId) {
      this.resetPreview()
      this.labelLookupRequestId += 1
      const requestId = this.labelLookupRequestId
      if (!ycLabelId || !this.selectedCompanyId) return

      const companyId = this.selectedCompanyId
      this.labelLookupLoading = true
      let failure = 'Не удалось проверить критерий'
      try {
        const data = await getRatingCriteria({
          company_id: companyId,
          yc_label_id: ycLabelId,
        })
        if (this.labelLookupRequestId !== requestId) return
        if (this.selectedCompanyId !== companyId || this.selectedLabelId !== ycLabelId) return

        const existing = ((data && data.results) || [])[0]
        if (existing) {
          failure = 'Не удалось открыть критерий'
          const criterion = existing.id ? await getRatingCriterion(existing.id) : existing
          if (this.labelLookupRequestId !== requestId) return
          if (this.selectedCompanyId !== companyId || this.selectedLabelId !== ycLabelId) return
          this.addDialog = false
          this.$nextTick(() => {
            this.showCriterionForm(criterion)
          })
          return
        }

        await this.loadPreview(companyId, ycLabelId)
      } catch (error) {
        if (this.labelLookupRequestId !== requestId) return
        this.previewError = apiErrorMessage(error, failure)
      } finally {
        if (this.labelLookupRequestId === requestId) this.labelLookupLoading = false
      }
    },
    async loadPreview(companyId, ycLabelId) {
      const requestId = this.previewRequestId + 1
      this.previewRequestId = requestId
      this.previewLoading = true
      this.previewError = ''
      try {
        const data = await getRatingPreview({ companyId, ycLabelId })
        if (this.previewRequestId !== requestId) return
        if (this.selectedCompanyId !== companyId || this.selectedLabelId !== ycLabelId) return

        const events = mapPreviewEvents(data && data.events)
        this.previewEvents = events
        this.previewPayload = data
        this.previewPeriod = formatPreviewPeriod(data)
        this.previewMessage = events.length
          ? ''
          : ((data && data.message) || 'События с этим тегом на ближайшие 30 дней не найдены')
        this.previewReady = true
      } catch (error) {
        if (this.previewRequestId !== requestId) return
        this.previewEvents = []
        this.previewPayload = null
        this.previewPeriod = ''
        this.previewReady = false
        this.previewMessage = ''
        this.previewError = apiErrorMessage(error, 'Не удалось загрузить события')
      } finally {
        if (this.previewRequestId === requestId) this.previewLoading = false
      }
    },
    openTagEvents(item) {
      this.eventsDialogTitle = item.label_title
        ? `События тега «${item.label_title}»`
        : 'События'
      this.eventsDialogCompany = item.company_title || ''
      this.eventsDialog = true
      this.loadTagEvents(item.company_id, item.yc_label_id)
    },
    applyTagEvents(data) {
      const events = mapPreviewEvents(data && data.events)
      this.tagEvents = events
      this.tagEventsPeriod = formatPreviewPeriod(data)
      this.tagEventsError = ''
      this.tagEventsLoading = false
      this.tagEventsMessage = events.length
        ? ''
        : ((data && data.message) || 'События с этим тегом на ближайшие 30 дней не найдены')
    },
    async loadTagEvents(companyId, ycLabelId) {
      const requestId = this.tagEventsRequestId + 1
      this.tagEventsRequestId = requestId
      this.tagEventsLoading = true
      this.tagEventsError = ''
      this.tagEvents = []
      this.tagEventsMessage = ''
      this.tagEventsPeriod = ''
      try {
        const data = await getRatingPreview({ companyId, ycLabelId })
        if (this.tagEventsRequestId !== requestId) return
        this.applyTagEvents(data)
      } catch (error) {
        if (this.tagEventsRequestId !== requestId) return
        this.tagEvents = []
        this.tagEventsPeriod = ''
        this.tagEventsMessage = ''
        this.tagEventsError = apiErrorMessage(error, 'Не удалось загрузить события')
      } finally {
        if (this.tagEventsRequestId === requestId) this.tagEventsLoading = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.coach-name-filter {
  flex: 0 0 180px;
  width: 180px;
  max-width: 180px;
  margin-bottom: 0;
  border-radius: 0;

  &.primary--text {
    color: inherit !important;
  }

  ::v-deep {
    .v-input__control > .v-input__slot {
      min-height: 28px !important;
      padding: 0 8px !important;
      border-radius: 0;
    }

    fieldset {
      border-radius: 0;
      border-width: 1px;
    }

    input {
      font-size: 13px;
      line-height: 18px;
    }

    .v-input__append-inner {
      margin-top: 0 !important;
      align-self: center;
    }

    .v-input__icon--clear .v-icon {
      font-size: 16px;
    }
  }
}

.theme--light .coach-name-filter ::v-deep fieldset {
  border-color: rgba(0, 0, 0, 0.18) !important;
}

.theme--dark .coach-name-filter ::v-deep fieldset {
  border-color: rgba(255, 255, 255, 0.22) !important;
}

.tag-events-table ::v-deep .v-data-table__wrapper {
  max-height: 320px;
  overflow-y: auto;
}
</style>
